/**
 * The call window and the ring card.
 *
 * Drawn above everything (the provider puts it in a portal on <body>), so the
 * person keeps working underneath — a work order open, the map moving — with
 * the call in a corner. Three sizes:
 *
 *  - a pill: who, how long, mute and hang up; press it to bring the call back;
 *  - a floating card (the default): dragged by its header, kept inside the
 *    screen, and remembered where it was left;
 *  - expanded: large and centred, for a group or a face that matters.
 *
 * "Keep on top of everything" goes further than the page can: Chrome and
 * Edge open the call as its own always-on-top window (Document
 * Picture-in-Picture), which stays above other applications too; Safari
 * floats the main video instead (video Picture-in-Picture). Where neither
 * exists the button is not shown.
 *
 * The other people's sound plays through <audio> elements kept in the main
 * page whatever the window is doing, so minimising, expanding or popping
 * out never interrupts what somebody is saying.
 *
 * On the sample nobody is on the other end. The tile of a simulated person
 * says so in words — "Sample call — nobody is on this line" — because a
 * demonstration that let somebody believe they had reached a colleague would
 * be the failure the sample banner exists to prevent.
 *
 * v2, for meetings: expanded, the window gains a side panel (people, chat,
 * settings — CallPanels.tsx); the floating card's People and Chat buttons
 * open it large. Raise hand, Share screen (where the browser can — phones
 * cannot, so the button is not drawn there), and for a host "End for all"
 * beside Leave. Whoever shares is the big tile, shown whole (contain, never
 * cropped) and labelled; the rest go to a strip. Above everything, beside the
 * ring cards: the meeting reminders, the lobby I am waiting in, and a
 * moment's notice — a host muted me, I was let in, a call was held by Do not
 * disturb.
 */
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useCallDirectory, useCallPhoto } from "../../api/queries";
import {
  duration,
  handQueue,
  initials,
  LEVEL_LABEL,
  lobby,
  moderates,
  roleOf,
  sharer,
  type Call,
  type Participant,
} from "../../domain/calls";
import { locale, t, tn, useI18n } from "../../i18n";
import { useCalls, type CallsApi, type Reminder } from "./CallsProvider";
import { SidePanel } from "./CallPanels";
import { onCall, type PeerView } from "./engine";
import s from "./CallWindow.module.css";

export const FLOAT_W = 360;
export const FLOAT_H = 300;
const POS_KEY = "sky.calls.position";
const MARGIN = 0;

/** Keep a window of w × h inside a viewport of vw × vh. */
export function clampPosition(x: number, y: number, w: number, h: number, vw: number, vh: number) {
  return {
    x: Math.round(Math.min(Math.max(MARGIN, x), Math.max(MARGIN, vw - w - MARGIN))),
    y: Math.round(Math.min(Math.max(MARGIN, y), Math.max(MARGIN, vh - h - MARGIN))),
  };
}

function readPos(): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(POS_KEY);
    const p = raw ? (JSON.parse(raw) as { x: unknown; y: unknown }) : null;
    return p && typeof p.x === "number" && typeof p.y === "number" ? { x: p.x, y: p.y } : null;
  } catch {
    return null;
  }
}

function writePos(p: { x: number; y: number }) {
  try {
    localStorage.setItem(POS_KEY, JSON.stringify(p));
  } catch {
    /* storage unavailable: the window returns to its corner next time */
  }
}

/** Below the header, at the reading end: top right, or top left in Arabic. */
function defaultPos(rtl: boolean) {
  const vw = window.innerWidth;
  return { x: rtl ? 20 : vw - FLOAT_W - 20, y: 68 };
}

/* ── small parts ──────────────────────────────────────────────────────── */

const ICON = {
  phone:
    "M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1 1 .4 1.9.7 2.8a2 2 0 01-.5 2.1L8 9.9a16 16 0 006 6l1.3-1.3a2 2 0 012.1-.4c.9.3 1.8.6 2.8.7a2 2 0 011.7 2z",
  hangup:
    "M3.7 14.6l-1.4-1.4a1.6 1.6 0 010-2.3C5.2 8 8.5 6.5 12 6.5s6.8 1.5 9.7 4.4a1.6 1.6 0 010 2.3l-1.4 1.4a1.6 1.6 0 01-2.1.2l-2-1.4a1.6 1.6 0 01-.7-1.3v-2a12 12 0 00-7 0v2a1.6 1.6 0 01-.7 1.3l-2 1.4a1.6 1.6 0 01-2.1-.2z",
  mic: "M12 3a3 3 0 00-3 3v6a3 3 0 006 0V6a3 3 0 00-3-3zM5 11a7 7 0 0014 0M12 18v3",
  micOff:
    "M3 3l18 18M9 9v3a3 3 0 005.1 2.1M15 9.3V6a3 3 0 00-5.7-1.3M5 11a7 7 0 0011.5 5.4M19 11a7 7 0 01-.6 2.8M12 18v3",
  cam: "M3 7h12v10H3zM15 10.5L21 7v10l-6-3.5",
  camOff: "M3 3l18 18M15 10.5L21 7v10l-4-2.3M9 7h6v6M3 7h2v10h10v-1",
  voice: "M3 12h2M7 8v8M11 5v14M15 8v8M19 11v2",
  addPerson: "M15 19c0-2.8-2.7-5-6-5s-6 2.2-6 5M9 11a3.5 3.5 0 100-7 3.5 3.5 0 000 7zM19 8v6M16 11h6",
  minimise: "M5 12h14",
  expand: "M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5",
  shrink: "M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5",
  popout: "M14 4h6v6M20 4l-8 8M18 14v5a1 1 0 01-1 1H5a1 1 0 01-1-1V7a1 1 0 011-1h5",
  close: "M6 6l12 12M18 6L6 18",
  hand: "M8 13V5.5a1.5 1.5 0 013 0V12M11 11.5V4a1.5 1.5 0 013 0v8M14 11.5V5.5a1.5 1.5 0 013 0V14c0 4-2.5 7-6.5 7-3 0-4.5-1.5-6-4l-2-3.5a1.5 1.5 0 012.5-1.6L8 13.5",
  screen: "M3 4h18v12H3zM8 20h8M12 16v4M9 10l3-3 3 3M12 7v6",
  stopShare: "M3 4h18v12H3zM8 20h8M12 16v4M9 7l6 6M15 7l-6 6",
  people:
    "M16 19c0-2.5-2.2-4.5-5-4.5S6 16.5 6 19M11 11.5a3 3 0 100-6 3 3 0 000 6zM17 11a2.5 2.5 0 100-5M21 19c0-2-1.4-3.6-3.3-4.2",
  chat: "M21 12a8 8 0 01-11.6 7.1L4 20.5l1.4-4.6A8 8 0 1121 12z",
  gear: "M12 15a3 3 0 100-6 3 3 0 000 6zM19.4 15a1.7 1.7 0 00.3 1.8l.1.1a2 2 0 01-2.8 2.8l-.1-.1a1.7 1.7 0 00-1.8-.3 1.7 1.7 0 00-1 1.5V21a2 2 0 01-4 0v-.1a1.7 1.7 0 00-1.1-1.5 1.7 1.7 0 00-1.8.3l-.1.1a2 2 0 01-2.8-2.8l.1-.1a1.7 1.7 0 00.3-1.8 1.7 1.7 0 00-1.5-1H3a2 2 0 010-4h.1a1.7 1.7 0 001.5-1.1 1.7 1.7 0 00-.3-1.8l-.1-.1a2 2 0 012.8-2.8l.1.1a1.7 1.7 0 001.8.3H9a1.7 1.7 0 001-1.5V3a2 2 0 014 0v.1a1.7 1.7 0 001 1.5 1.7 1.7 0 001.8-.3l.1-.1a2 2 0 012.8 2.8l-.1.1a1.7 1.7 0 00-.3 1.8V9a1.7 1.7 0 001.5 1H21a2 2 0 010 4h-.1a1.7 1.7 0 00-1.5 1z",
  lock: "M6 11h12v9H6zM8.5 11V8a3.5 3.5 0 017 0v3",
  moon: "M20 14.5A8 8 0 019.5 4a8 8 0 1010.5 10.5z",
  calendar: "M4 6h16v14H4zM4 10h16M8 3v4M16 3v4",
  endAll:
    "M3.7 14.6l-1.4-1.4a1.6 1.6 0 010-2.3C5.2 8 8.5 6.5 12 6.5s6.8 1.5 9.7 4.4a1.6 1.6 0 010 2.3l-1.4 1.4a1.6 1.6 0 01-2.1.2l-2-1.4a1.6 1.6 0 01-.7-1.3v-2a12 12 0 00-7 0v2a1.6 1.6 0 01-.7 1.3l-2 1.4a1.6 1.6 0 01-2.1.2zM8 20h8",
};

function Ico({ d, size = 20 }: { d: string; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={d} />
    </svg>
  );
}

/** A colour of its own for each person, so a grid of initials is told apart at a glance. */
function hue(name: string): number {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % 360;
  return h;
}

export function Face({
  name,
  employeeId,
  hasPhoto,
  size = 72,
  ring,
}: {
  name: string;
  employeeId?: string;
  hasPhoto: boolean;
  size?: number;
  ring?: boolean;
}) {
  const photo = useCallPhoto(employeeId, hasPhoto);
  const h = hue(name);
  return (
    <span
      className={`${s.face} ${ring ? s.faceRing : ""}`}
      style={{ "--face": `${size}px`, "--hue": h } as CSSProperties}
      aria-hidden="true"
    >
      {photo.data ? <img src={photo.data} alt="" /> : <span>{initials(name)}</span>}
    </span>
  );
}

function StreamVideo({
  stream,
  mirror,
  videoRef,
  className,
}: {
  stream: MediaStream | null;
  mirror?: boolean;
  videoRef?: (v: HTMLVideoElement | null) => void;
  className?: string;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v || !stream) return;
    if (v.srcObject !== stream) v.srcObject = stream;
    const p = v.play?.() as Promise<void> | undefined;
    if (p && typeof p.catch === "function") p.catch(() => undefined);
  }, [stream]);
  return (
    <video
      ref={(v) => {
        ref.current = v;
        videoRef?.(v);
      }}
      className={`${s.video} ${mirror ? s.mirror : ""} ${className ?? ""}`}
      autoPlay
      playsInline
      muted
    />
  );
}

function PeerAudio({ stream }: { stream: MediaStream | null }) {
  const ref = useRef<HTMLAudioElement | null>(null);
  useEffect(() => {
    const a = ref.current;
    if (!a || !stream) return;
    if (a.srcObject !== stream) a.srcObject = stream;
    const p = a.play?.() as Promise<void> | undefined;
    if (p && typeof p.catch === "function") p.catch(() => undefined);
  }, [stream]);
  return <audio ref={ref} autoPlay />;
}

function useNow(ms: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}

const hasVideoTrack = (s: MediaStream | null | undefined) =>
  !!s && typeof s.getVideoTracks === "function" && s.getVideoTracks().length > 0;

/* ── the layer ────────────────────────────────────────────────────────── */

export function CallLayer() {
  const calls = useCalls();
  if (!calls) return null;
  const { state } = calls;
  return (
    <>
      <div className={s.rings} data-testid="call-rings">
        {state.incoming.map((c) => (
          <RingCard key={c.id} call={c} calls={calls} />
        ))}
        {state.waiting && <WaitingCard calls={calls} call={state.waiting} />}
      </div>
      {/* meeting reminders: their own corner, clear of the header's menus, the call window and Sky's dock;
          a reminder waits while a call is on */}
      {!state.call && calls.reminders.length > 0 && (
        <div className={s.reminders} data-testid="call-reminders">
          {/* one at a time — live first — so a reminder never buries the page */}
          <ReminderCard
            key={calls.reminders[0].key}
            calls={calls}
            r={calls.reminders[0]}
            more={calls.reminders.length - 1}
          />
        </div>
      )}
      {state.call && <CallWindow calls={calls} call={state.call} />}
      {/* their voices, kept in the main page whatever the window does */}
      {state.call && Object.entries(state.peers).map(([id, p]) => <PeerAudio key={id} stream={p.stream} />)}
      {!state.call && state.ended && (
        <div className={s.toast} role="status" data-testid="call-ended">
          {t("Call ended")}
          {state.ended.answeredAt && state.ended.endedAt && (
            <> · {duration((Date.parse(state.ended.endedAt) - Date.parse(state.ended.answeredAt)) / 1000)}</>
          )}
        </div>
      )}
      {!state.call && state.notice && !state.ended && (
        <div className={s.toast} role="status" data-testid="call-notice">
          <span className={s.toastText}>{t(state.notice.text, state.notice.vars)}</span>
          <button type="button" onClick={() => calls.engine.clearNotice()} aria-label={t("Close")}>
            <Ico d={ICON.close} size={16} />
          </button>
        </div>
      )}
      {!state.call && state.error && (
        <div className={`${s.toast} ${s.toastError}`} role="alert" data-testid="call-error">
          <span>{state.error}</span>
          <button type="button" onClick={() => calls.engine.clearError()} aria-label={t("Close")}>
            <Ico d={ICON.close} size={16} />
          </button>
        </div>
      )}
    </>
  );
}

/* ── the lobby I wait in, and meeting reminders ───────────────────────── */

function WaitingCard({ calls, call }: { calls: CallsApi; call: Call }) {
  const host = call.participants.find((p) => p.principalId === (call.host ?? call.startedBy));
  const title = call.title || host?.name || t("A group call");
  return (
    <section className={s.ring} role="status" aria-label={t("Waiting to join")} data-testid="call-waiting">
      <div className={s.ringHead}>
        <span className={s.waitSpinner} aria-hidden="true" />
        <div className={s.ringWho}>
          <b>{title}</b>
          <span>{t("Waiting for the host to let you in…")}</span>
          {host && <span>{t("Host: {name}", { name: host.name })}</span>}
          {call.sample && <em className={s.sampleTag}>{t("Sample call — nobody is on this line")}</em>}
        </div>
      </div>
      <div className={s.ringActions}>
        <button
          type="button"
          className={s.addGo}
          style={{ background: "var(--call-btn)" }}
          onClick={() => void calls.engine.cancelWaiting()}
          data-testid="waiting-cancel"
        >
          {t("Cancel")}
        </button>
      </div>
    </section>
  );
}

function ReminderCard({ calls, r, more = 0 }: { calls: CallsApi; r: Reminder; more?: number }) {
  const navigate = useNavigate();
  const m = r.meeting;
  const now = useNow(15000);
  const mins = Math.max(0, Math.round((Date.parse(m.startsAt) - now) / 60000));
  const when =
    r.stage === "live"
      ? t("Live now")
      : mins === 0
        ? t("Starting now")
        : tn(mins, "Starts in {n} min", "Starts in {n} min");
  const busy = calls.state.busy || !!calls.state.call;
  const video = m.media === "video";
  const go = async () => {
    let ok = false;
    if (m.state === "live" && m.callId) ok = await calls.engine.join(m.callId, video);
    else if (m.canStart) ok = await calls.engine.startMeeting(m.id, video);
    else {
      navigate("/calls");
      ok = true;
    }
    if (ok) calls.dismissReminder(r.key);
  };
  const action = m.state === "live" ? t("Join") : m.canStart ? t("Start") : t("Open");
  return (
    <section
      className={`${s.ring} ${s.reminder}`}
      role="status"
      aria-label={t("Meeting reminder")}
      data-testid={`reminder-${m.id}`}
    >
      <div className={s.ringHead}>
        <span className={`${s.reminderIcon} ${r.stage === "live" ? s.reminderLive : ""}`} aria-hidden="true">
          <Ico d={ICON.calendar} size={22} />
        </span>
        <div className={s.ringWho}>
          <b>{m.title}</b>
          <span>
            <span className={r.stage === "live" ? s.liveText : undefined}>{when}</span> ·{" "}
            {new Date(m.startsAt).toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" })}
            {m.organiserName && <> · {t("by {name}", { name: m.organiserName })}</>}
          </span>
          <span className={s.faces}>
            {m.invitees.slice(0, 5).map((i) => (
              <Face key={i.principalId} name={i.name} employeeId={i.employeeId} hasPhoto={i.hasPhoto} size={24} />
            ))}
            {m.invitees.length > 5 && <span className={s.moreFaces}>+{m.invitees.length - 5}</span>}
          </span>
          {m.sample && <em className={s.sampleTag}>{t("Sample meeting")}</em>}
        </div>
      </div>
      <div className={s.reminderActions}>
        {more > 0 && <span className={s.moreReminders}>{tn(more, "+{n} more reminder", "+{n} more reminders")}</span>}
        <button
          type="button"
          className={s.pill}
          onClick={() => calls.dismissReminder(r.key)}
          data-testid="reminder-dismiss"
        >
          {t("Dismiss")}
        </button>
        <button
          type="button"
          className={`${s.pill} ${s.pillGreen}`}
          disabled={busy}
          title={calls.state.call ? t("Finish the call you are on first.") : undefined}
          onClick={() => void go()}
          data-testid="reminder-join"
        >
          {action}
        </button>
      </div>
    </section>
  );
}

/* ── ringing ──────────────────────────────────────────────────────────── */

function RingCard({ call, calls }: { call: Call; calls: CallsApi }) {
  const caller = call.participants.find((p) => p.principalId === call.startedBy);
  const name = call.startedByName ?? caller?.name ?? t("A colleague");
  const others = call.participants.filter((p) => p.principalId !== call.startedBy && p.state !== "declined").length - 1;
  const busy = calls.state.busy;
  return (
    <section
      className={s.ring}
      role="alertdialog"
      aria-label={t("Incoming call from {name}", { name })}
      data-testid={`ring-${call.id}`}
    >
      <div className={s.ringHead}>
        <Face name={name} employeeId={caller?.employeeId} hasPhoto={!!caller?.hasPhoto} size={56} ring />
        <div className={s.ringWho}>
          <b>{name}</b>
          <span>
            {call.media === "video" ? t("Video call") : t("Voice call")}
            {caller?.level && <> · {t(LEVEL_LABEL[caller.level])}</>}
            {others > 0 && <> · {tn(others, "{n} other on the call", "{n} others on the call")}</>}
          </span>
          {call.sample && <em className={s.sampleTag}>{t("Sample call — nobody is on this line")}</em>}
        </div>
      </div>
      <div className={s.ringActions}>
        <RoundButton
          label={t("Decline")}
          tone="red"
          icon={ICON.hangup}
          disabled={busy}
          onClick={() => void calls.engine.decline(call.id)}
          testId="ring-decline"
        />
        <RoundButton
          label={t("Voice")}
          tone="green"
          icon={ICON.phone}
          disabled={busy}
          onClick={() => void calls.engine.accept(call.id, false)}
          testId="ring-voice"
        />
        <RoundButton
          label={t("Video")}
          tone="green"
          icon={ICON.cam}
          disabled={busy}
          onClick={() => void calls.engine.accept(call.id, true)}
          testId="ring-video"
        />
      </div>
      <div className={s.ringFoot}>
        <label>
          <input type="checkbox" checked={calls.ringMuted} onChange={(e) => calls.setRingMuted(e.target.checked)} />{" "}
          {t("Mute the ringtone")}
        </label>
      </div>
    </section>
  );
}

function RoundButton({
  label,
  icon,
  tone,
  onClick,
  disabled,
  pressed,
  testId,
  showLabel = true,
  title,
  badge,
}: {
  label: string;
  icon: string;
  tone?: "green" | "red" | "plain";
  onClick: () => void;
  disabled?: boolean;
  pressed?: boolean;
  testId?: string;
  showLabel?: boolean;
  title?: string;
  /** a count on the button: unread chat, people waiting */
  badge?: number;
}) {
  return (
    <span className={s.roundWrap}>
      <button
        type="button"
        className={`${s.round} ${tone === "green" ? s.green : tone === "red" ? s.red : ""} ${pressed ? s.on : ""}`}
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        aria-pressed={pressed}
        title={title ?? label}
        data-testid={testId}
      >
        <Ico d={icon} />
        {badge ? (
          <span className={s.roundBadge} data-testid={testId ? `${testId}-badge` : undefined}>
            {badge > 9 ? "9+" : badge}
          </span>
        ) : null}
      </button>
      {showLabel && <span className={s.roundLabel}>{label}</span>}
    </span>
  );
}

/* ── the window ───────────────────────────────────────────────────────── */

interface DocPip {
  requestWindow(o: { width: number; height: number }): Promise<Window>;
  window: Window | null;
}

function docPip(): DocPip | undefined {
  return (window as unknown as { documentPictureInPicture?: DocPip }).documentPictureInPicture;
}

/** Put this page's styles into a new window, so the call looks the same there. */
function copyStyles(target: Document) {
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      const css = Array.from(sheet.cssRules)
        .map((r) => r.cssText)
        .join("\n");
      const style = target.createElement("style");
      style.textContent = css;
      target.head.appendChild(style);
    } catch {
      if (sheet.href) {
        const link = target.createElement("link");
        link.rel = "stylesheet";
        link.href = sheet.href;
        target.head.appendChild(link);
      }
    }
  }
  const root = document.documentElement;
  for (const a of ["dir", "lang", "data-theme"]) {
    const v = root.getAttribute(a);
    if (v) target.documentElement.setAttribute(a, v);
  }
}

function CallWindow({ calls, call }: { calls: CallsApi; call: Call }) {
  const { dir } = useI18n();
  const { size } = calls;
  const [pos, setPos] = useState(() => {
    const p = readPos() ?? defaultPos(dir === "rtl");
    return clampPosition(p.x, p.y, FLOAT_W, FLOAT_H, window.innerWidth, window.innerHeight);
  });
  /* the latest position, for the release that saves it — a state read there can be a render behind */
  const posRef = useRef(pos);
  const winRef = useRef<HTMLElement | null>(null);
  const drag = useRef<{ dx: number; dy: number; w: number; h: number } | null>(null);
  const [pip, setPip] = useState<Window | null>(null);
  const mainVideo = useRef<HTMLVideoElement | null>(null);

  /* the screen got smaller: bring the window back inside it */
  useEffect(() => {
    const onResize = () => {
      const p = posRef.current;
      const el = winRef.current;
      const next = clampPosition(
        p.x,
        p.y,
        el?.offsetWidth || FLOAT_W,
        el?.offsetHeight || FLOAT_H,
        window.innerWidth,
        window.innerHeight,
      );
      posRef.current = next;
      setPos(next);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* the call ended with the window popped out: close it */
  useEffect(() => () => pip?.close(), [pip]);

  const onPointerDown = (e: ReactPointerEvent<HTMLElement>) => {
    if (size !== "float" || (e.target as HTMLElement).closest("button")) return;
    const el = winRef.current;
    drag.current = {
      dx: e.clientX - pos.x,
      dy: e.clientY - pos.y,
      w: el?.offsetWidth || FLOAT_W,
      h: el?.offsetHeight || FLOAT_H,
    };
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* a synthetic pointer has nothing to capture */
    }
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d) return;
    const next = clampPosition(e.clientX - d.dx, e.clientY - d.dy, d.w, d.h, window.innerWidth, window.innerHeight);
    posRef.current = next;
    setPos(next);
  };
  const onPointerUp = () => {
    if (!drag.current) return;
    drag.current = null;
    writePos(posRef.current);
  };

  const canDocPip = !!docPip();
  const canVideoPip =
    typeof document !== "undefined" &&
    !!(document as Document & { pictureInPictureEnabled?: boolean }).pictureInPictureEnabled;
  const popOut = async () => {
    const dp = docPip();
    if (dp) {
      try {
        const w = await dp.requestWindow({ width: 420, height: 340 });
        copyStyles(w.document);
        w.document.body.className = s.pipBody;
        w.addEventListener("pagehide", () => setPip(null));
        setPip(w);
      } catch {
        /* refused (no user gesture, or another picture-in-picture open) */
      }
      return;
    }
    const v = mainVideo.current as (HTMLVideoElement & { requestPictureInPicture?: () => Promise<unknown> }) | null;
    if (v?.requestPictureInPicture) await v.requestPictureInPicture().catch(() => undefined);
  };
  const popTitle = t(
    "Keep this call on top of everything, even other applications. Chrome and Edge open it as its own always-on-top window; Safari floats the video.",
  );

  const body = (
    <WindowBody
      calls={calls}
      call={call}
      inPip={!!pip}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      popOut={canDocPip || canVideoPip ? () => void popOut() : undefined}
      popTitle={popTitle}
      mainVideo={(v) => (mainVideo.current = v)}
    />
  );

  if (pip) {
    return (
      <>
        {createPortal(<div className={`${s.window} ${s.pip}`}>{body}</div>, pip.document.body)}
        <div className={s.mini} role="status" data-testid="call-popped">
          <span className={s.miniDot} />
          <span>{t("The call is in its own window")}</span>
          <button type="button" className={s.miniBack} onClick={() => pip.close()}>
            {t("Bring it back")}
          </button>
        </div>
      </>
    );
  }

  if (size === "mini") return <MiniPill calls={calls} call={call} />;

  const style: CSSProperties = size === "float" ? { left: pos.x, top: pos.y } : {};
  return (
    <section
      ref={winRef}
      className={`${s.window} ${size === "large" ? s.large : s.float}`}
      style={style}
      role="dialog"
      aria-label={t("Staff call")}
      data-testid="call-window"
      data-size={size}
      dir={dir}
    >
      {body}
    </section>
  );
}

function names(call: Call, me: string | undefined): string {
  const o = onCall(call, me);
  if (call.title) return call.title;
  if (o.length === 0) return t("Nobody else is on the call");
  if (o.length === 1) return o[0].name;
  return `${o[0].name} +${o.length - 1}`;
}

function useTimer(call: Call): string {
  const now = useNow(1000);
  const from = call.answeredAt ?? call.createdAt;
  return duration((now - Date.parse(from)) / 1000);
}

function MiniPill({ calls, call }: { calls: CallsApi; call: Call }) {
  const { state, engine } = calls;
  const timer = useTimer(call);
  const outgoing = call.state === "ringing" && call.startedBy === state.me;
  return (
    <div className={s.mini} data-testid="call-mini">
      <button
        type="button"
        className={s.miniMain}
        onClick={() => calls.setSize("float")}
        aria-label={t("Restore the call window")}
        data-testid="call-restore"
      >
        <span className={s.miniDot} />
        <b>{names(call, state.me)}</b>
        <span className={s.miniTime}>{outgoing ? t("Calling…") : timer}</span>
      </button>
      <button
        type="button"
        className={`${s.miniBtn} ${!state.local.audioOn ? s.on : ""}`}
        onClick={() => void engine.setMuted(state.local.audioOn)}
        aria-label={state.local.audioOn ? t("Mute") : t("Unmute")}
        aria-pressed={!state.local.audioOn}
      >
        <Ico d={state.local.audioOn ? ICON.mic : ICON.micOff} size={16} />
      </button>
      <button
        type="button"
        className={`${s.miniBtn} ${s.red}`}
        onClick={() => void engine.hangup()}
        aria-label={t("Hang up")}
        data-testid="mini-hangup"
      >
        <Ico d={ICON.hangup} size={16} />
      </button>
    </div>
  );
}

function WindowBody({
  calls,
  call,
  inPip,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  popOut,
  popTitle,
  mainVideo,
}: {
  calls: CallsApi;
  call: Call;
  inPip: boolean;
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void;
  onPointerUp: () => void;
  popOut?: () => void;
  popTitle: string;
  mainVideo: (v: HTMLVideoElement | null) => void;
}) {
  const { state, engine, size, setSize } = calls;
  const me = state.me;
  const people = onCall(call, me);
  const outgoing = call.state === "ringing" && call.startedBy === me;
  const timer = useTimer(call);
  const [adding, setAdding] = useState(false);
  const local = state.local;
  const sample = !!call.sample;
  const mod = moderates(call, me);
  const group = call.kind === "group" || people.length > 1;
  const waiting = mod ? lobby(call).length : 0;
  const unread = state.messages.filter((m) => m.id > state.chatRead && m.from !== me).length;
  const myHand = !!call.participants.find((p) => p.principalId === me)?.handRaised || !!call.me.handRaised;
  const panel = size === "large" && !inPip ? calls.panel : null;
  const togglePanel = (p: "people" | "chat") => calls.openPanel(calls.panel === p && size === "large" ? null : p);
  const canShare = calls.engine.canShare() && !inPip;

  return (
    <>
      <header
        className={s.head}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        data-testid="call-drag"
      >
        <div className={s.headWho}>
          <b>{names(call, me)}</b>
          <span className={s.headSub}>
            {outgoing ? t("Ringing…") : <span data-testid="call-timer">{timer}</span>}
            {" · "}
            {local.mode === "video" ? t("Video") : t("Voice")}
            {people.length > 1 && <> · {tn(people.length + 1, "{n} person", "{n} people")}</>}
            {mod && group && <> · {t(roleOf(call, me) === "host" ? "Host" : "Co-host")}</>}
          </span>
        </div>
        <div className={s.headTools}>
          {call.locked && (
            <span className={s.headLock} title={t("Locked: nobody new can join")} data-testid="call-locked">
              <Ico d={ICON.lock} size={15} />
            </span>
          )}
          {!inPip && (
            <button
              type="button"
              className={s.headBtn}
              onClick={() => calls.openPanel(calls.panel === "settings" && size === "large" ? null : "settings")}
              aria-label={t("Call settings")}
              title={t("Call settings")}
              data-testid="call-settings-open"
            >
              <Ico d={ICON.gear} size={15} />
            </button>
          )}
          {popOut && !inPip && (
            <button
              type="button"
              className={s.headBtn}
              onClick={popOut}
              aria-label={t("Keep on top of everything")}
              title={popTitle}
              data-testid="call-popout"
            >
              <Ico d={ICON.popout} size={16} />
            </button>
          )}
          {!inPip && (
            <button
              type="button"
              className={s.headBtn}
              onClick={() => setSize("mini")}
              aria-label={t("Minimise")}
              title={t("Minimise")}
              data-testid="call-minimise"
            >
              <Ico d={ICON.minimise} size={16} />
            </button>
          )}
          {!inPip && (
            <button
              type="button"
              className={s.headBtn}
              onClick={() => setSize(size === "large" ? "float" : "large")}
              aria-label={size === "large" ? t("Restore size") : t("Expand")}
              title={size === "large" ? t("Restore size") : t("Expand")}
              data-testid="call-expand"
            >
              <Ico d={size === "large" ? ICON.shrink : ICON.expand} size={16} />
            </button>
          )}
        </div>
      </header>

      <div className={s.main} data-panel={panel ? "open" : undefined}>
        <Stage calls={calls} call={call} people={people} outgoing={outgoing} mainVideo={mainVideo} />
        {panel && <SidePanel calls={calls} call={call} panel={panel} />}
      </div>

      {/* a sample call says so on each stand-in's tile; while it rings out, here */}
      {((sample && (outgoing || people.length > 1)) || state.notes.length > 0 || state.error || state.notice) && (
        <div className={s.notes} data-testid="call-notes">
          {sample && (outgoing || people.length > 1) && (
            <span className={s.sampleTag}>{t("Sample call — nobody is on this line")}</span>
          )}
          {state.notes.map((n) => (
            <span key={n}>{t(n)}</span>
          ))}
          {state.notice && (
            <span className={s.noteNotice} data-testid="call-window-notice">
              {t(state.notice.text, state.notice.vars)}
            </span>
          )}
          {state.error && <span className={s.noteError}>{state.error}</span>}
        </div>
      )}

      <footer className={s.controls}>
        <RoundButton
          label={local.audioOn ? t("Mute") : t("Unmute")}
          icon={local.audioOn ? ICON.mic : ICON.micOff}
          pressed={!local.audioOn}
          onClick={() => void engine.setMuted(local.audioOn)}
          showLabel={size === "large"}
          testId="call-mute"
        />
        {local.mode === "video" && (
          <RoundButton
            label={local.videoOn ? t("Turn camera off") : t("Turn camera on")}
            icon={local.videoOn ? ICON.cam : ICON.camOff}
            pressed={!local.videoOn}
            onClick={() => void engine.setVideo(!local.videoOn)}
            showLabel={size === "large"}
            testId="call-camera"
          />
        )}
        <RoundButton
          label={local.mode === "video" ? t("Switch to voice") : t("Switch to video")}
          icon={local.mode === "video" ? ICON.voice : ICON.cam}
          onClick={() => void engine.switchMode(local.mode === "video" ? "audio" : "video")}
          showLabel={size === "large"}
          testId="call-mode"
        />
        <RoundButton
          label={t("Add people")}
          icon={ICON.addPerson}
          pressed={adding}
          onClick={() => {
            /* the floating card has no room for a list of people: choosing them opens the call large */
            if (!adding && size === "float") setSize("large");
            setAdding((a) => !a);
          }}
          showLabel={size === "large"}
          testId="call-add"
        />
        {canShare && (
          <RoundButton
            label={local.sharing ? t("Stop sharing") : t("Share screen")}
            icon={local.sharing ? ICON.stopShare : ICON.screen}
            pressed={!!local.sharing}
            disabled={outgoing}
            onClick={() => void (local.sharing ? engine.stopShare() : engine.startShare())}
            showLabel={size === "large"}
            testId="call-share"
          />
        )}
        {size === "large" && (
          <RoundButton
            label={myHand ? t("Lower hand") : t("Raise hand")}
            icon={ICON.hand}
            pressed={myHand}
            onClick={() => void engine.raiseHand(!myHand)}
            testId="call-hand"
          />
        )}
        {!inPip && (
          <RoundButton
            label={t("People")}
            icon={ICON.people}
            pressed={panel === "people"}
            badge={waiting}
            onClick={() => togglePanel("people")}
            showLabel={size === "large"}
            testId="call-people"
          />
        )}
        {!inPip && (
          <RoundButton
            label={t("Chat")}
            icon={ICON.chat}
            pressed={panel === "chat"}
            badge={panel === "chat" ? 0 : unread}
            onClick={() => togglePanel("chat")}
            showLabel={size === "large"}
            testId="call-chat"
          />
        )}
        <RoundButton
          label={outgoing ? t("Cancel call") : mod && group ? t("Leave") : t("Hang up")}
          icon={ICON.hangup}
          tone="red"
          onClick={() => void engine.hangup()}
          showLabel={size === "large"}
          testId="call-hangup"
        />
        {mod && group && size === "large" && !outgoing && (
          <RoundButton
            label={t("End for all")}
            icon={ICON.endAll}
            tone="red"
            onClick={() => void engine.endForAll()}
            testId="call-end-all"
          />
        )}
      </footer>
      {adding && <AddPeople calls={calls} call={call} close={() => setAdding(false)} />}
    </>
  );
}

function Stage({
  calls,
  call,
  people,
  outgoing,
  mainVideo,
}: {
  calls: CallsApi;
  call: Call;
  people: Participant[];
  outgoing: boolean;
  mainVideo: (v: HTMLVideoElement | null) => void;
}) {
  const { state } = calls;
  const local = state.local;
  const self = (
    <SelfTile stream={local.stream} videoOn={local.videoOn && local.mode === "video"} muted={!local.audioOn} />
  );
  const order = new Map(handQueue(call).map((p, i) => [p.principalId, i + 1]));

  /* somebody's screen is the big tile, whole; everyone else goes to a strip */
  const shown = sharer(call);
  const remote = shown && shown.principalId !== state.me ? shown : undefined;
  if (local.sharing || remote) {
    const stream = local.sharing ? (local.screen ?? null) : (state.peers[remote!.principalId]?.stream ?? null);
    const label = local.sharing
      ? t("You are sharing your screen")
      : t("{name} is sharing their screen", { name: remote!.name });
    return (
      <div className={`${s.stage} ${s.spot}`} data-testid="call-spotlight">
        <div className={s.spotMain}>
          {hasVideoTrack(stream) ? (
            <StreamVideo stream={stream} className={s.contain} videoRef={local.sharing ? undefined : mainVideo} />
          ) : (
            <div className={s.calling}>{t("Waiting for the shared screen…")}</div>
          )}
          <div className={s.spotLabel}>
            <Ico d={ICON.screen} size={14} /> {label}
          </div>
          {local.sharing && (
            <div className={s.shareBanner} data-testid="share-banner">
              <span>{t("You are sharing your screen")}</span>
              <button type="button" onClick={() => void calls.engine.stopShare()} data-testid="share-stop">
                {t("Stop")}
              </button>
            </div>
          )}
        </div>
        <div className={s.strip}>
          {people
            .filter((p) => p.state === "joined")
            .map((p) => (
              <Tile
                key={p.principalId}
                p={p}
                peer={state.peers[p.principalId]}
                mode={local.mode}
                hand={order.get(p.principalId)}
                small
              />
            ))}
          <div className={`${s.tile} ${s.stripSelf}`}>{self}</div>
        </div>
      </div>
    );
  }

  if (outgoing && people.length <= 1) {
    const p = people[0];
    return (
      <div className={s.stage}>
        <div className={s.calling}>
          {p && <Face name={p.name} employeeId={p.employeeId} hasPhoto={p.hasPhoto} size={84} ring />}
          <span>{t("Calling {name}…", { name: p?.name ?? "" })}</span>
        </div>
        <div className={s.selfCorner}>{self}</div>
      </div>
    );
  }

  if (people.length <= 1) {
    const p = people[0];
    return (
      <div className={s.stage}>
        {p ? (
          <Tile
            p={p}
            peer={state.peers[p.principalId]}
            mode={local.mode}
            big
            mainVideo={mainVideo}
            hand={order.get(p.principalId)}
          />
        ) : (
          <div className={s.calling}>{t("Nobody else is on the call")}</div>
        )}
        <div className={s.selfCorner}>{self}</div>
      </div>
    );
  }

  return (
    <div className={`${s.stage} ${s.grid}`} data-count={people.length + 1}>
      {people.map((p, i) => (
        <Tile
          key={p.principalId}
          p={p}
          peer={state.peers[p.principalId]}
          mode={local.mode}
          mainVideo={i === 0 ? mainVideo : undefined}
          hand={order.get(p.principalId)}
        />
      ))}
      <div className={s.tile}>{self}</div>
    </div>
  );
}

function SelfTile({ stream, videoOn, muted }: { stream: MediaStream | null; videoOn: boolean; muted: boolean }) {
  return (
    <div className={s.self} data-testid="call-self">
      {videoOn && hasVideoTrack(stream) ? (
        <StreamVideo stream={stream} mirror />
      ) : (
        <span className={s.selfLabel}>{t("You")}</span>
      )}
      {muted && (
        <span className={s.mutedBadge} title={t("Muted")}>
          <Ico d={ICON.micOff} size={13} />
        </span>
      )}
    </div>
  );
}

function Tile({
  p,
  peer,
  mode,
  big,
  mainVideo,
  hand,
  small,
}: {
  p: Participant;
  peer?: PeerView;
  mode: "audio" | "video";
  big?: boolean;
  mainVideo?: (v: HTMLVideoElement | null) => void;
  /** their place in the line of raised hands */
  hand?: number;
  /** a tile in the strip under a shared screen */
  small?: boolean;
}) {
  const showVideo = ((mode === "video" && p.videoOn) || !!p.sharing) && hasVideoTrack(peer?.stream);
  let status: string | null = null;
  /* a small tile has room for a word; the window's notes line carries the sentence */
  if (p.simulated) status = big ? t("Sample call — nobody is on this line") : t("Sample stand-in");
  else if (p.state === "ringing") status = t("Ringing…");
  else if (!peer || peer.status === "connecting") status = t("Connecting…");
  else if (peer.status === "reconnecting") status = t("Reconnecting…");
  else if (peer.status === "failed") status = t("Could not connect");
  return (
    <div
      className={`${s.tile} ${big ? s.tileBig : ""} ${small ? s.tileSmall : ""}`}
      data-testid={`tile-${p.principalId}`}
    >
      {showVideo ? (
        <StreamVideo stream={peer!.stream} videoRef={mainVideo} className={p.sharing ? s.contain : undefined} />
      ) : (
        <div className={s.tileFace}>
          <Face
            name={p.name}
            employeeId={p.employeeId}
            hasPhoto={p.hasPhoto}
            size={small ? 36 : big ? 72 : 52}
            ring={p.state === "ringing"}
          />
        </div>
      )}
      {hand && (
        <span className={s.tileHand} title={t("Hand raised")} data-testid={`hand-${p.principalId}`}>
          ✋ {hand}
        </span>
      )}
      <div className={s.tileLabel}>
        <b>{p.name}</b>
        {!p.audioOn && (
          <span className={s.mutedBadge} title={t("Muted")}>
            <Ico d={ICON.micOff} size={13} />
          </span>
        )}
      </div>
      {status && <div className={s.tileStatus}>{status}</div>}
    </div>
  );
}

function AddPeople({ calls, call, close }: { calls: CallsApi; call: Call; close: () => void }) {
  const dir = useCallDirectory();
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const on = new Set(
    call.participants.filter((p) => p.state === "joined" || p.state === "ringing").map((p) => p.principalId),
  );
  const max = dir.data?.summary?.groupMax ?? 6;
  const room = Math.max(0, max - on.size);
  const people = useMemo(
    () =>
      (dir.data?.items ?? []).filter(
        (p) => !on.has(p.principalId) && (!q || p.name.toLowerCase().includes(q.toLowerCase())),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dir.data, q, call.participants],
  );
  const canGroup = dir.data?.summary?.canGroup ?? true;
  const ring = async () => {
    if (await calls.engine.invite(picked)) close();
  };
  return (
    <div className={s.add} data-testid="call-add-panel">
      <div className={s.addHead}>
        <b>{t("Add people")}</b>
        <span>{tn(room, "room for {n} more", "room for {n} more")}</span>
        <button type="button" className={s.headBtn} onClick={close} aria-label={t("Close")}>
          <Ico d={ICON.close} size={14} />
        </button>
      </div>
      {!canGroup ? (
        <p className={s.addNote}>{t("Your level may not start group calls.")}</p>
      ) : (
        <>
          <input
            className={s.addSearch}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("Search a name")}
            aria-label={t("Search a name")}
          />
          <ul className={s.addList}>
            {people.map((p) => {
              const checked = picked.includes(p.principalId);
              const full = !checked && picked.length >= room;
              return (
                <li key={p.principalId}>
                  <label title={p.canCall ? undefined : p.reason ? t(p.reason) : undefined}>
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!p.canCall || full}
                      onChange={() =>
                        setPicked((x) => (checked ? x.filter((y) => y !== p.principalId) : [...x, p.principalId]))
                      }
                    />
                    <span className={`${s.dot} ${p.online ? s.dotOn : ""}`} />
                    <span>{p.name}</span>
                    {!p.canCall && <em>{p.reason ? t(p.reason) : t("Not allowed")}</em>}
                  </label>
                </li>
              );
            })}
          </ul>
          <button
            type="button"
            className={s.addGo}
            disabled={!picked.length || calls.state.busy}
            onClick={() => void ring()}
          >
            {picked.length ? tn(picked.length, "Ring {n} person", "Ring {n} people") : t("Choose who to ring")}
          </button>
        </>
      )}
    </div>
  );
}

export function CallIcon({ d = ICON.phone, size }: { d?: string; size?: number }): ReactNode {
  return <Ico d={d} size={size} />;
}
export const CALL_ICONS = ICON;
